import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Compass, Lightning, Cpu, Target, ArrowRight, Sparkle, ChatCircle } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import usePageMeta from "@/hooks/usePageMeta";

const SUGGESTED = [
  "Mobile Auto Detailing", "Notary Service", "AirBnB Cleaning Crew", "AI Marketing Agency",
  "Pressure Washing", "Pet Grooming Van", "Bookkeeping Service", "Drone Photography",
  "Vending Machine Route", "Personal Chef", "Yoga Studio", "Junk Removal",
];

const DIRECTOR_INTRO = [
  "Commander, take a breath.",
  "I'm your Sovereign Director. I'm going to walk you through this — one tiny step at a time.",
  "Tell me what kind of operation you want to commission. Anything. Service, product, creative, industrial.",
  "We'll commission the legal hull, scout your domain, deploy your Ghost Crew, arm you with the right hardware & software, then ignite the first signal. You won't be alone.",
];

export default function BlueprintGenerator() {
  usePageMeta(
    "AI Blueprint Engine",
    "Generate a Sovereign Business Blueprint for any niche. AI-powered, step-by-step: legal setup, Ghost Crew deployment, and marketing strikes."
  );
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0=intro, 1=form, 2=generating
  const [businessType, setBusinessType] = useState("");
  const [location, setLocation] = useState("");
  const [audience, setAudience] = useState("");
  const [introIdx, setIntroIdx] = useState(0);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (step !== 0) return;
    if (introIdx >= DIRECTOR_INTRO.length - 1) return;
    const t = setTimeout(() => setIntroIdx((i) => i + 1), 1800);
    return () => clearTimeout(t);
  }, [introIdx, step]);

  const generate = async () => {
    if (!businessType.trim()) {
      toast.error("Commander, give me a sector to work with.");
      return;
    }
    setLoading(true);
    setStep(2);
    try {
      const { data } = await api.post("/blueprint/generate", {
        business_type: businessType,
        location,
        target_audience: audience,
        budget_level: "near-zero",
      });
      // Async job pattern — poll until complete
      const jobId = data.job_id;
      const maxAttempts = 45; // 45 * 3s = 135s max
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        if (attempts > maxAttempts) {
          toast.error("Director took too long. Try again.");
          setStep(1);
          setLoading(false);
          return;
        }
        try {
          const { data: job } = await api.get(`/blueprint/job/${jobId}`);
          if (job.status === "complete" && job.blueprint_id) {
            navigate(`/blueprint/${job.blueprint_id}`);
            return;
          }
          if (job.status === "failed") {
            toast.error("Director offline: " + (job.error || "Unknown failure"));
            setStep(1);
            setLoading(false);
            return;
          }
          setTimeout(poll, 3000);
        } catch (e) {
          setTimeout(poll, 3500);
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Director temporarily offline. Try again.");
      setStep(1);
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16 min-h-[80vh]" data-testid="blueprint-generator-page">
      {/* Intro stage */}
      {step === 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <div className="w-20 h-20 border border-[#FFD700] rotate-45 flex items-center justify-center mx-auto mb-8 animate-pulse-gold">
            <Compass size={36} weight="duotone" className="text-[#FFD700] -rotate-45" />
          </div>
          <span className="overline">// The Sovereign Director</span>
          <h1 className="heading-display text-4xl md:text-6xl mt-3 mb-12">Blueprint Engine</h1>

          <div className="space-y-5 max-w-2xl mx-auto mb-12 min-h-[200px]">
            <AnimatePresence>
              {DIRECTOR_INTRO.slice(0, introIdx + 1).map((line, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="flex items-start gap-4 text-left"
                >
                  <ChatCircle size={20} weight="duotone" className="text-[#FFD700] flex-shrink-0 mt-1" />
                  <p className="text-base md:text-lg text-white/80 font-light leading-relaxed">{line}</p>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {introIdx >= DIRECTOR_INTRO.length - 1 && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onClick={() => setStep(1)}
              data-testid="director-continue-btn"
              className="btn-gold px-8 py-4 text-xs inline-flex items-center gap-3"
            >
              I'm ready, Director
              <ArrowRight size={16} weight="bold" />
            </motion.button>
          )}
        </motion.div>
      )}

      {/* Form */}
      {step === 1 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-12">
            <span className="overline">// Mission Briefing</span>
            <h1 className="heading-display text-4xl md:text-5xl mt-3 mb-4">
              Tell me about your <span className="text-[#FFD700]">operation</span>.
            </h1>
            <p className="text-white/60 font-light">Any sector. Any vision. I'll build the armored blueprint.</p>
          </div>

          <div className="surface-card p-8 md:p-10 space-y-6">
            <div>
              <Label className="overline text-[0.6rem] mb-2 block">Business Type *</Label>
              <Input
                data-testid="bp-business-type"
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
                placeholder="e.g., Mobile Auto Detailing, AI Marketing Agency, Drone Photography..."
                className="bg-[#0c0c0c] border-white/10 rounded-none h-14 text-white text-base"
              />
              <div className="mt-4">
                <div className="overline text-[0.55rem] text-white/40 mb-2">// Suggested Sectors</div>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTED.map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`bp-suggested-${s.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => setBusinessType(s)}
                      className={`text-[0.65rem] uppercase tracking-wider px-3 py-1.5 border transition-all ${
                        businessType === s
                          ? "border-[#FFD700] text-[#FFD700] bg-[#FFD700]/10"
                          : "border-white/10 text-white/50 hover:border-white/30 hover:text-white"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label className="overline text-[0.6rem] mb-2 block">Location <span className="text-white/30 normal-case">(optional)</span></Label>
                <Input
                  data-testid="bp-location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Atlanta, GA"
                  className="bg-[#0c0c0c] border-white/10 rounded-none h-12 text-white"
                />
              </div>
              <div>
                <Label className="overline text-[0.6rem] mb-2 block">Target Audience <span className="text-white/30 normal-case">(optional)</span></Label>
                <Input
                  data-testid="bp-audience"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder="Luxury car owners"
                  className="bg-[#0c0c0c] border-white/10 rounded-none h-12 text-white"
                />
              </div>
            </div>

            <div className="pt-4 border-t border-white/5 grid sm:grid-cols-3 gap-4">
              {[
                { icon: Lightning, t: "Zero-Cap Hull" },
                { icon: Cpu, t: "Ghost Crew" },
                { icon: Target, t: "Signal Strikes" },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-3 text-xs text-white/50">
                  <s.icon size={18} weight="duotone" className="text-[#FFD700]" />
                  <span className="uppercase tracking-wider font-semibold">{s.t}</span>
                </div>
              ))}
            </div>

            <button
              onClick={generate}
              disabled={loading || !businessType.trim()}
              data-testid="bp-generate-btn"
              className="w-full btn-gold py-5 text-xs flex items-center justify-center gap-3 disabled:opacity-50"
            >
              <Sparkle size={18} weight="duotone" />
              Commission Sovereign Blueprint
            </button>
          </div>
        </motion.div>
      )}

      {/* Generating */}
      {step === 2 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-32" data-testid="bp-generating">
          <div className="w-20 h-20 border border-[#FFD700] rotate-45 flex items-center justify-center mx-auto mb-8 animate-pulse-gold">
            <Compass size={36} weight="duotone" className="text-[#FFD700] -rotate-45 animate-spin" style={{ animationDuration: "3s" }} />
          </div>
          <span className="overline mb-4 block">// Director At Work</span>
          <h2 className="heading-display text-3xl md:text-4xl mb-6">
            Commissioning your<br />Sovereign Blueprint...
          </h2>
          <div className="space-y-3 max-w-md mx-auto">
            <DirectorLine delay={0}>Surveying market terrain...</DirectorLine>
            <DirectorLine delay={5000}>Mapping hull commissioning steps...</DirectorLine>
            <DirectorLine delay={15000}>Drafting why / how field intel...</DirectorLine>
            <DirectorLine delay={25000}>Deploying Ghost Crew agents...</DirectorLine>
            <DirectorLine delay={35000}>Quartermaster arming niche arsenal...</DirectorLine>
            <DirectorLine delay={45000}>Calibrating signal strikes...</DirectorLine>
            <DirectorLine delay={55000}>Sealing the armored blueprint...</DirectorLine>
          </div>
        </motion.div>
      )}
    </div>
  );
}

const DirectorLine = ({ children, delay }) => {
  const [show, setShow] = useState(false);
  React.useEffect(() => { const t = setTimeout(() => setShow(true), delay); return () => clearTimeout(t); }, [delay]);
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={show ? { opacity: 1, x: 0 } : {}}
      className="flex items-center gap-3 text-sm text-white/60 font-mono tracking-wider"
    >
      <span className="text-[#FFD700]">›</span>
      {children}
    </motion.div>
  );
};
